import React from 'react'
import { useMutation } from '@apollo/client'
import { Button } from '@winnin/winnin_ui.components.button'
import { InputWithLabel } from '@winnin/winnin_ui.components.input-with-label'
import { Modal } from '@winnin/winnin_ui.components.modal'
import { Title } from '@winnin/winnin_ui.components.title'
import { useToast } from '@winnin/winnin_ui.components.toast'

import LinkToast from 'components/Toasts/LinkToast'
import { useDashboardModals } from 'contexts/DashboardModalsContext'
import { T, useTranslate } from 'lib/tolgee'
import { PanelStore } from 'stores'
import { TOLGEE_GLOBALS, analyticsTracker, captalizeFirstLetter } from 'utils'

import { EDIT_DASHBOARD_MUTATION } from './query'
import './index.scss'
import TOLGEE_KEYS from './translate'

const { EDIT_JOB, CLOSE_MODAL } = analyticsTracker.eventNames

const MODAL_STYLE = {
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.80)',
    overflowY: 'overlay',
    zIndex: 999,
  },
  content: {
    width: '80%',
    maxWidth: '564px',
    position: 'absolute',
    top: '50%',
    right: 'unset',
    left: '50%',
    bottom: 'unset',
    transform: 'translate(-50%, -50%)',
    border: 'none',
    padding: 'var(--spacing_unit_6)',
    overflow: 'unset',
    borderRadius: '8px',
  },
}

const getSuccessToastMessage = ({ jobName = '', isEdit, jobType }) => {
  const capitalizedJobType = captalizeFirstLetter(jobType)
  const keyObj = TOLGEE_KEYS[isEdit ? 'successfullyEditedToast' : 'successfullyRenamedToast']({
    jobType: capitalizedJobType,
    jobName,
  })

  return (
    <p>
      <T
        keyName={keyObj.key}
        parameters={{
          ...keyObj.parameters,
          strong: <strong className="toast-jobName" />,
        }}
      >
        {keyObj.defaultValue}
      </T>
    </p>
  )
}

export default function EditDashboardModal() {
  const t = useTranslate()
  const { createToast } = useToast()
  const { dashboard, setDashboard, isEditDashboardModalOpen, setIsEditDashboardModalOpen } = useDashboardModals()

  const workspaceId = PanelStore?.selectedPanel?.id

  const { jobType } = dashboard

  const isEditFlow = dashboard?.description

  const [editDashboard, { loading }] = useMutation(EDIT_DASHBOARD_MUTATION, {
    onCompleted({ editWorkspaceJob }) {
      setDashboard({})
      setIsEditDashboardModalOpen(false)

      const infoToast = {
        message: getSuccessToastMessage({ jobName: dashboard?.name, isEdit: isEditFlow, jobType }),
        link: `/workspace/${workspaceId}/jobs/${dashboard?.id}/dashboard`,
      }

      analyticsTracker.eventTrack(EDIT_JOB, {
        'Job Name': editWorkspaceJob?.name,
        'Job Type': editWorkspaceJob?.type,
        'Job ID': editWorkspaceJob?.id,
      })

      createToast({
        content: <LinkToast toast={infoToast} />,
      })
    },
    refetchQueries: [
      'workspaceJobsWithFoldersPaginated',
      'addToGetWorkspaceJobsPaginated',
      'getListWorkspaceJobPaginated',
    ],
    onError(error) {
      console.error('EditDashboardModal @ editDashboard', error)

      createToast({
        content: t(TOLGEE_GLOBALS.requestProblem),
      })
    },
  })

  function handleCloseModal() {
    analyticsTracker.eventTrack(CLOSE_MODAL, {
      modalType: isEditFlow ? 'Job Edit' : 'Job Rename',
    })

    setDashboard({})
    setIsEditDashboardModalOpen(false)
  }

  const handleNameChange = e => {
    setDashboard(prevState => ({
      ...prevState,
      name: e.target.value,
    }))
  }

  const handleGoalChange = e => {
    setDashboard(prevState => ({
      ...prevState,
      description: e.target.value,
    }))
  }

  const handleEditButtonClick = () => {
    editDashboard({
      variables: {
        workspaceJobInput: {
          id: dashboard?.id,
          name: dashboard?.name,
          description: dashboard?.description,
        },
      },
    })
  }

  return (
    <Modal
      isOpen={isEditDashboardModalOpen}
      style={MODAL_STYLE}
      onRequestClose={handleCloseModal}
      appElement={document.body}
    >
      <div className="EditDashboardModal">
        <Title tag="h3">{t(TOLGEE_KEYS.modalTitle({ jobType }))}</Title>

        <InputWithLabel
          label={t(TOLGEE_KEYS.modalNameLabel({ jobType }))}
          onChange={e => handleNameChange(e)}
          value={dashboard?.name}
          maxLength={40}
        />

        {dashboard?.description !== null && (
          <InputWithLabel
            label={t(TOLGEE_KEYS.modalDescriptionLabel({ jobType }))}
            onChange={e => handleGoalChange(e)}
            value={dashboard?.description}
            maxLength={140}
          />
        )}

        <div className="EditDashboardModal-footer">
          <Button onClick={handleCloseModal} variation="faded" appearance="link">
            {t(TOLGEE_GLOBALS.cancel)}
          </Button>
          <Button onClick={handleEditButtonClick} disabled={loading}>
            {t(TOLGEE_GLOBALS.edit)}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
